import { useEffect, useState, Suspense } from "react";
import axios from "axios";
import {
  Cpu,
  Droplets,
  Flame,
  Leaf,
  Server,
  Globe2,
  ArrowRight,
  ArrowUpRight,
  Compass,
  MoveRight,
  CheckCircle2,
  Globe,
  Zap,
  Thermometer,
} from "lucide-react";
import EnergyScene from "@/components/EnergyScene";
import { useCountUp } from "@/hooks/useCountUp";
import { useI18n } from "@/hooks/useI18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* -------------------- Language Toggle -------------------- */
function LangToggle({ locale, toggle, t }) {
  return (
    <button
      onClick={toggle}
      aria-label={t.lang.label}
      title={t.lang.label}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-stone-300/70 bg-white/70 backdrop-blur-md text-xs font-medium text-stone-800 hover:bg-white transition-colors"
      data-testid="lang-toggle"
    >
      <Globe className="w-3.5 h-3.5" strokeWidth={1.6} />
      <span className="uppercase tracking-wider">{locale === "en" ? "EN" : "DE"}</span>
      <span className="text-stone-400">/</span>
      <span className="text-stone-500">{locale === "en" ? "DE" : "EN"}</span>
    </button>
  );
}

/* -------------------- Brand mark -------------------- */
function BrandMark({ t, size = "md" }) {
  const dot = size === "sm" ? "w-8 h-8" : "w-9 h-9";
  const icon = size === "sm" ? "w-4 h-4" : "w-4.5 h-4.5";
  const text = size === "sm" ? "text-lg" : "text-xl";
  return (
    <div className="flex items-center gap-2.5">
      <div className={`${dot} rounded-full bg-[#1B4332] flex items-center justify-center relative`}>
        <Cpu className={`${icon} text-[#95D5B2]`} strokeWidth={1.6} />
      </div>
      <span className={`font-display ${text} font-medium tracking-tight text-stone-900`}>
        {t.brand.name}
        <span className="text-[#1B4332]">{t.brand.suffix}</span>
      </span>
    </div>
  );
}

/* -------------------- Nav -------------------- */
function Nav({ onCtaClick, t, locale, toggle }) {
  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-stone-50/70 border-b border-stone-200/60"
      data-testid="main-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-16">
        <a href="#top" className="flex items-center" data-testid="nav-logo">
          <BrandMark t={t} size="sm" />
        </a>
        <div className="hidden md:flex items-center gap-9 text-sm text-stone-700">
          <a href="#platform" className="hover:text-stone-900 transition-opacity opacity-70 hover:opacity-100" data-testid="nav-platform">{t.nav.platform}</a>
          <a href="#impact" className="hover:text-stone-900 transition-opacity opacity-70 hover:opacity-100" data-testid="nav-impact">{t.nav.impact}</a>
          <a href="#how" className="hover:text-stone-900 transition-opacity opacity-70 hover:opacity-100" data-testid="nav-how">{t.nav.how}</a>
          <a href="#contact" className="hover:text-stone-900 transition-opacity opacity-70 hover:opacity-100" data-testid="nav-contact">{t.nav.contact}</a>
        </div>
        <div className="flex items-center gap-3">
          <LangToggle locale={locale} toggle={toggle} t={t} />
          <button
            onClick={onCtaClick}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-[#1B4332] text-stone-50 text-sm font-medium hover:-translate-y-0.5 hover:bg-[#2D6A4F] transition-all shadow-sm"
            data-testid="nav-cta-button"
          >
            {t.nav.cta}
            <ArrowRight className="w-3.5 h-3.5" strokeWidth={2} />
          </button>
        </div>
      </div>
    </nav>
  );
}

/* -------------------- Live stat card -------------------- */
function StatTile({ label, value, unit, icon: Icon, testId, accent = false, decimals = 0, locale = "en", forceZero = false }) {
  const animated = useCountUp(typeof value === "number" ? value : 0);
  const displayValue = forceZero ? 0 : animated;
  const display =
    typeof value === "number"
      ? displayValue.toLocaleString(locale === "de" ? "de-DE" : "en-US", {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })
      : "—";

  return (
    <div
      className={`relative p-5 rounded-2xl border ${
        accent
          ? "bg-[#1B4332] border-[#1B4332] text-stone-50"
          : "bg-white/70 backdrop-blur-md border-white/60 text-stone-900"
      } shadow-sm`}
      data-testid={testId}
    >
      <div className="flex items-center justify-between mb-3">
        <span className={`text-[10px] font-bold tracking-[0.22em] uppercase ${accent ? "text-[#95D5B2]" : "text-[#1B4332]"}`}>
          {label}
        </span>
        <Icon className={`w-4 h-4 ${accent ? "text-[#95D5B2]" : "text-stone-500"}`} strokeWidth={1.5} />
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className="font-mono-num text-3xl md:text-4xl font-light tracking-tighter">
          {display}
        </span>
        {unit && (
          <span className={`text-xs font-medium ${accent ? "text-[#95D5B2]/80" : "text-stone-500"}`}>
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

/* -------------------- Hero -------------------- */
function Hero({ stats, onCtaClick, t, locale }) {
  return (
    <section id="top" className="relative pt-16 min-h-screen overflow-hidden">
      {/* 3D scene background */}
      <div className="absolute inset-0 z-0" data-testid="hero-3d-canvas">
        <Suspense fallback={<div className="w-full h-full bg-[#DDECD6]" />}>
          <EnergyScene />
        </Suspense>
      </div>

      {/* Readability gradients */}
      <div className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-r from-stone-50 via-stone-50/70 to-transparent" />
      <div className="absolute inset-0 z-[1] pointer-events-none bg-gradient-to-b from-stone-50/40 via-transparent to-stone-50/40" />

      {/* Text overlay */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-14 lg:pt-20 pb-10 pointer-events-none">
        <div className="max-w-2xl rise pointer-events-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 backdrop-blur-md border border-white/60 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#2D6A4F] live-dot" />
            <span className="text-[11px] font-medium tracking-wide text-stone-700" data-testid="hero-badge">
              {t.hero.badge}
            </span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-stone-900 leading-[0.95]" data-testid="hero-title">
            {t.hero.title1}
            <br />
            <span className="italic text-[#1B4332]">{t.hero.titleItalic}</span> {t.hero.title2}
          </h1>
          <p className="mt-6 text-stone-700 text-lg leading-relaxed max-w-xl">
            {t.hero.sub}
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <button
              onClick={onCtaClick}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1B4332] text-stone-50 font-medium hover:-translate-y-0.5 hover:bg-[#2D6A4F] transition-all shadow-md"
              data-testid="hero-primary-cta"
            >
              {t.hero.ctaPrimary}
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </button>
            <a
              href="#platform"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-stone-300/80 bg-white/60 backdrop-blur-md text-stone-900 font-medium hover:bg-white transition-all"
              data-testid="hero-secondary-cta"
            >
              {t.hero.ctaSecondary}
              <MoveRight className="w-4 h-4" strokeWidth={2} />
            </a>
          </div>
        </div>
      </div>

      {/* Live stats overlay — HPC pillars */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4" data-testid="hero-stats-grid">
          <StatTile
            label={t.hero.stats.gpus}
            value={stats?.gpus_online}
            unit={t.hero.stats.gpusUnit}
            icon={Cpu}
            testId="stat-gpus-online"
            accent
            decimals={0}
            locale={locale}
          />
          <StatTile
            label={t.hero.stats.pflops}
            value={stats?.pflops_active}
            unit={t.hero.stats.pflopsUnit}
            icon={Server}
            testId="stat-pflops-active"
            decimals={1}
            locale={locale}
          />
          <StatTile
            label={t.hero.stats.heat}
            value={stats?.heat_recovered_kwh}
            unit={t.hero.stats.heatUnit}
            icon={Flame}
            testId="stat-heat-recovered"
            decimals={0}
            locale={locale}
          />
          <StatTile
            label={t.hero.stats.wue}
            value={stats?.wue}
            unit={t.hero.stats.wueUnit}
            icon={Droplets}
            testId="stat-wue"
            decimals={2}
            locale={locale}
            forceZero
          />
        </div>
        <p className="mt-3 text-[11px] text-stone-600 tracking-wide">
          <Compass className="inline w-3 h-3 mr-1 -mt-0.5" strokeWidth={1.5} />
          {t.hero.hint}
        </p>
      </div>
    </section>
  );
}

/* -------------------- Numbers vs Industry Comparison Strip -------------------- */
function ComparisonStrip({ t }) {
  // Numeric parsing for the bar visualisation (handles "1.8", "1,8", "100%", "100 %")
  const parseNum = (v) => {
    if (typeof v !== "string") return 0;
    const cleaned = v.replace(/[%\s]/g, "").replace(",", ".");
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  return (
    <section
      className="relative -mt-20 z-20 max-w-7xl mx-auto px-6 lg:px-10 pointer-events-none"
      data-testid="comparison-strip"
    >
      <div className="pointer-events-auto bg-white/95 backdrop-blur-xl border border-stone-200 rounded-3xl shadow-2xl shadow-stone-900/10 p-6 md:p-8 lg:p-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 md:gap-10 mb-8 md:mb-10">
          <div className="max-w-xl">
            <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#1B4332]">
              {t.comparison.overline}
            </span>
            <h2 className="mt-3 font-display text-2xl md:text-3xl font-medium tracking-tight text-stone-900">
              {t.comparison.title}
            </h2>
            <p className="mt-3 text-sm text-stone-600 leading-relaxed">
              {t.comparison.sub}
            </p>
          </div>
          <div className="flex items-center gap-5 text-xs shrink-0">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#1B4332]" />
              <span className="font-medium text-stone-800">{t.comparison.aethronLabel}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-stone-300" />
              <span className="text-stone-500">{t.comparison.industryLabel}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 lg:gap-10">
          {t.comparison.metrics.map((m) => {
            const aeth = parseNum(m.aethron);
            const ind = parseNum(m.industry);
            const scale = Math.max(aeth, ind, 0.01);
            // For "lower is better" metrics (WUE, PUE), Aethron's bar is short and green.
            // For "higher is better" (Renewable), Aethron's bar is long.
            const aethWidth = `${Math.max(2, (aeth / scale) * 100)}%`;
            const indWidth = `${Math.max(2, (ind / scale) * 100)}%`;
            return (
              <div
                key={m.key}
                className="border-t border-stone-200 pt-5"
                data-testid={`comparison-metric-${m.key}`}
              >
                <div className="flex items-baseline justify-between mb-1">
                  <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-stone-500">
                    {m.name}
                  </span>
                  {m.unit && (
                    <span className="text-[10px] font-mono-num text-stone-400 tracking-wider normal-case">
                      {m.unit}
                    </span>
                  )}
                </div>

                <div className="flex items-baseline gap-3 mb-1">
                  <span className="font-mono-num text-4xl md:text-5xl font-light tracking-tighter text-[#1B4332]">
                    {m.aethron}
                  </span>
                  <span className="text-sm text-stone-400 font-mono-num">
                    vs&nbsp;{m.industry}
                  </span>
                </div>

                <div className="mt-4 space-y-1.5">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-[#1B4332] w-16 shrink-0 font-semibold">
                      {t.comparison.aethronLabel}
                    </span>
                    <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[#1B4332] rounded-full transition-all duration-700"
                        style={{ width: aethWidth }}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-stone-400 w-16 shrink-0">
                      {t.comparison.industryLabel}
                    </span>
                    <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-stone-300 rounded-full transition-all duration-700"
                        style={{ width: indWidth }}
                      />
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-xs text-stone-500 leading-relaxed">{m.caption}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* -------------------- Logos strip -------------------- */
function LogosStrip({ t }) {
  const logos = [
    "MPI Meteorology",
    "Fraunhofer IEE",
    "CERN openlab",
    "TenneT",
    "50Hertz",
    "ETH Zürich",
    "DLR",
  ];
  return (
    <section className="border-y border-stone-200 bg-stone-100/60 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 flex items-center gap-10">
        <span className="text-[10px] font-bold tracking-[0.24em] uppercase text-stone-500 shrink-0">
          {t.logos.trusted}
        </span>
        <div className="relative overflow-hidden flex-1">
          <div className="flex gap-14 marquee-track w-max">
            {[...logos, ...logos].map((l, i) => (
              <span key={i} className="font-display text-xl text-stone-400 whitespace-nowrap">
                {l}
              </span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Platform bento (4 pillars) -------------------- */
function Platform({ t }) {
  return (
    <section id="platform" className="py-24 lg:py-32 bg-stone-50 relative grain">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#1B4332]">
            {t.platform.overline}
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-stone-900">
            {t.platform.title}
          </h2>
          <p className="mt-5 text-stone-600 leading-relaxed">
            {t.platform.sub}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-6" data-testid="platform-grid">
          {/* PILLAR 1 — HPC (large) */}
          <div className="md:col-span-6 lg:col-span-7 rounded-3xl bg-[#0F2A1D] text-stone-50 p-10 relative overflow-hidden" data-testid="pillar-hpc">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#1B4332] text-[10px] font-bold tracking-[0.2em] uppercase text-[#95D5B2] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#95D5B2]" />
              Pillar 01
            </div>
            <Cpu className="w-8 h-8 text-[#95D5B2] mb-6" strokeWidth={1.4} />
            <h3 className="font-display text-2xl lg:text-3xl font-medium tracking-tight">
              {t.platform.hpc.title}
            </h3>
            <p className="mt-4 text-stone-300 max-w-md leading-relaxed">
              {t.platform.hpc.body}
            </p>
            <ul className="mt-8 space-y-3 text-sm text-stone-200">
              {t.platform.hpc.bullets.map((i) => (
                <li key={i} className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-[#95D5B2]" strokeWidth={1.5} />
                  {i}
                </li>
              ))}
            </ul>
            {/* Decorative rack graphic */}
            <div className="absolute right-6 bottom-6 hidden md:flex gap-1 opacity-70">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="w-2 h-16 rounded-sm bg-[#95D5B2]/30 relative overflow-hidden">
                  <div
                    className="absolute inset-x-0 bottom-0 bg-[#95D5B2] rounded-sm"
                    style={{ height: `${45 + ((i * 13) % 45)}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="absolute -bottom-14 -right-14 w-64 h-64 rounded-full bg-[#1B4332] opacity-40 blur-2xl" />
          </div>

          {/* PILLAR 2 — WUE 0.0 */}
          <div className="md:col-span-6 lg:col-span-5 rounded-3xl bg-white border border-stone-200 p-10 relative overflow-hidden" data-testid="pillar-wue">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#D8F3DC] text-[10px] font-bold tracking-[0.2em] uppercase text-[#1B4332] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1B4332]" />
              Pillar 02
            </div>
            <Droplets className="w-8 h-8 text-[#1B4332] mb-6" strokeWidth={1.4} />
            <h3 className="font-display text-2xl font-medium tracking-tight text-stone-900">
              {t.platform.wue.title}
            </h3>
            <p className="mt-4 text-stone-600 leading-relaxed">
              {t.platform.wue.body}
            </p>
            <div className="mt-8 flex items-end gap-6">
              <div>
                <div className="font-mono-num text-6xl font-light tracking-tighter text-[#1B4332]">0.0</div>
                <div className="mt-1 text-[10px] font-bold tracking-[0.2em] uppercase text-stone-500">
                  L / kWh
                </div>
              </div>
              <div className="flex-1 pb-2">
                <div className="h-1 w-full bg-stone-200 rounded-full overflow-hidden">
                  <div className="h-full w-0 bg-[#1B4332]" />
                </div>
                <div className="mt-2 text-xs text-stone-500">vs. industry avg. 1.8 L/kWh</div>
              </div>
            </div>
          </div>

          {/* PILLAR 3 — Waste Heat */}
          <div className="md:col-span-3 lg:col-span-4 rounded-3xl bg-[#F1E9DB] p-10" data-testid="pillar-heat">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#E9C46A]/50 text-[10px] font-bold tracking-[0.2em] uppercase text-[#8a5a00] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8a5a00]" />
              Pillar 03
            </div>
            <Flame className="w-8 h-8 text-[#B45309]" strokeWidth={1.4} />
            <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-stone-900">
              {t.platform.heat.title}
            </h3>
            <p className="mt-3 text-stone-700 text-sm leading-relaxed">
              {t.platform.heat.body}
            </p>
          </div>

          {/* PILLAR 4 — Global Standard */}
          <div className="md:col-span-3 lg:col-span-8 rounded-3xl bg-[#D8F3DC] p-10 relative overflow-hidden" data-testid="pillar-global">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/70 text-[10px] font-bold tracking-[0.2em] uppercase text-[#1B4332] mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#1B4332]" />
              Pillar 04
            </div>
            <div className="flex items-start gap-4">
              <Globe2 className="w-8 h-8 text-[#1B4332] shrink-0" strokeWidth={1.4} />
              <div>
                <h3 className="font-display text-xl md:text-2xl font-medium tracking-tight text-stone-900">
                  {t.platform.global.title}
                </h3>
                <p className="mt-3 text-stone-700 text-sm md:text-base leading-relaxed max-w-xl">
                  {t.platform.global.body}
                </p>
                <div className="mt-6 flex flex-wrap items-center gap-6 text-xs text-stone-700">
                  <div className="flex items-center gap-2">
                    <Leaf className="w-4 h-4 text-[#1B4332]" strokeWidth={1.5} />
                    100% renewable · 24/7 matched
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#1B4332]" strokeWidth={1.5} />
                    7 sites live · EU · Nordics · NA
                  </div>
                </div>
              </div>
            </div>
            <div className="absolute -right-10 -bottom-10 w-40 h-40 rounded-full bg-[#95D5B2] opacity-40 blur-2xl" />
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Impact -------------------- */
function Impact({ t }) {
  return (
    <section id="impact" className="py-24 lg:py-32 bg-[#0A1A14] text-stone-50 relative overflow-hidden">
      <img
        src="https://images.pexels.com/photos/19708669/pexels-photo-19708669.jpeg"
        alt="Sunset turbines"
        className="absolute inset-0 w-full h-full object-cover opacity-20"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0A1A14] via-[#0A1A14]/85 to-[#0A1A14]/60" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#95D5B2]">
          {t.impact.overline}
        </span>
        <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-light tracking-tighter max-w-4xl">
          {t.impact.title}
        </h2>
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8 border-t border-stone-700/60 pt-10">
          {t.impact.stats.map((s, i) => (
            <div key={i} data-testid={`impact-stat-${i}`}>
              <div className="font-mono-num text-3xl md:text-5xl font-light tracking-tighter">
                {s.k}
              </div>
              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-stone-400">
                {s.v}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* -------------------- How it works -------------------- */
function HowItWorks({ t }) {
  const icons = [Zap, Cpu, Thermometer];
  return (
    <section id="how" className="py-24 lg:py-32 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-4">
            <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#1B4332]">
              {t.how.overline}
            </span>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-stone-900">
              {t.how.title}
            </h2>
          </div>
          <div className="lg:col-span-8 space-y-8">
            {t.how.steps.map((s, idx) => {
              const Icon = icons[idx] || Zap;
              return (
                <div key={s.n} className="flex gap-6 md:gap-10 border-t border-stone-200 pt-8" data-testid={`step-${s.n}`}>
                  <div className="flex items-start gap-4 shrink-0">
                    <div className="font-mono-num text-sm text-[#1B4332] pt-1">{s.n}</div>
                    <div className="w-10 h-10 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                      <Icon className="w-5 h-5 text-[#1B4332]" strokeWidth={1.5} />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-display text-2xl font-medium tracking-tight text-stone-900">
                      {s.t}
                    </h3>
                    <p className="mt-2 text-stone-600 leading-relaxed max-w-2xl">{s.d}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- Slot Picker -------------------- */
function SlotPicker({ t, locale, selectedSlot, onSelect }) {
  const [days, setDays] = useState(null);
  const [activeDay, setActiveDay] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/slots/available?days=14`);
        if (!alive) return;
        setDays(res.data.days || []);
        // Preselect first day that has an available slot
        const idx = (res.data.days || []).findIndex((d) =>
          d.times.some((tm) => tm.available)
        );
        setActiveDay(idx >= 0 ? idx : 0);
      } catch {
        if (alive) setError(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  // Locale-aware day label: "Thu 02 Jul" in EN, "Do 02 Juli" in DE
  const formatDayLabel = (isoDate) => {
    try {
      const d = new Date(`${isoDate}T12:00:00Z`);
      return d.toLocaleDateString(locale === "de" ? "de-DE" : "en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        timeZone: "Europe/Berlin",
      });
    } catch {
      return isoDate;
    }
  };

  if (loading) {
    return (
      <div className="text-sm text-stone-500" data-testid="slot-picker-loading">
        {t.contact.slot.loading}
      </div>
    );
  }

  if (error || !days || days.length === 0 || !days.some((d) => d.times.some((tm) => tm.available))) {
    return (
      <div className="text-sm text-stone-600 bg-stone-100 rounded-xl p-4" data-testid="slot-picker-empty">
        {t.contact.slot.noSlots}
      </div>
    );
  }

  const day = days[activeDay];

  return (
    <div data-testid="slot-picker">
      <label className="text-xs font-medium tracking-wide text-stone-600 mb-2 block">
        {t.contact.slot.pickDate}
      </label>
      <div className="flex flex-wrap gap-2 mb-5" data-testid="slot-days">
        {days.map((d, i) => {
          const any = d.times.some((tm) => tm.available);
          const active = i === activeDay;
          return (
            <button
              type="button"
              key={d.date}
              disabled={!any}
              onClick={() => setActiveDay(i)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-all ${
                active
                  ? "bg-[#1B4332] border-[#1B4332] text-stone-50"
                  : any
                  ? "bg-white border-stone-300 text-stone-800 hover:border-[#1B4332]"
                  : "bg-stone-100 border-stone-200 text-stone-400 cursor-not-allowed"
              }`}
              data-testid={`slot-day-${d.date}`}
            >
              {formatDayLabel(d.date)}
            </button>
          );
        })}
      </div>

      <label className="text-xs font-medium tracking-wide text-stone-600 mb-2 block">
        {t.contact.slot.pickTime}
      </label>
      <div className="grid grid-cols-3 gap-2" data-testid="slot-times">
        {day.times.map((tm) => {
          const selected = selectedSlot === tm.iso;
          return (
            <button
              type="button"
              key={tm.iso}
              disabled={!tm.available}
              onClick={() => onSelect(tm.iso)}
              className={`px-3 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                selected
                  ? "bg-[#1B4332] border-[#1B4332] text-stone-50 ring-2 ring-[#95D5B2] ring-offset-1"
                  : tm.available
                  ? "bg-white border-stone-300 text-stone-800 hover:border-[#1B4332] hover:bg-stone-50"
                  : "bg-stone-100 border-stone-200 text-stone-400 line-through cursor-not-allowed"
              }`}
              data-testid={`slot-time-${tm.iso}`}
            >
              <div className="font-mono-num">{tm.time}</div>
              {!tm.available && (
                <div className="text-[9px] uppercase tracking-wider text-stone-400 mt-0.5">
                  {t.contact.slot.taken}
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* -------------------- Contact / CTA -------------------- */
function ContactForm({ t, locale }) {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "demo", note: "" });
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [status, setStatus] = useState({ state: "idle", msg: "" });

  const formatSelectedSlot = (iso) => {
    if (!iso) return null;
    try {
      const d = new Date(iso);
      return d.toLocaleString(locale === "de" ? "de-DE" : "en-GB", {
        weekday: "short",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "Europe/Berlin",
      }) + " CET";
    } catch {
      return iso;
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !selectedSlot) {
      setStatus({ state: "error", msg: t.contact.errorRequired });
      return;
    }
    setStatus({ state: "loading", msg: "" });
    try {
      await axios.post(`${API}/bookings`, { ...form, slot: selectedSlot });
      setStatus({ state: "ok", msg: t.contact.success });
      setForm({ name: "", email: "", company: "", interest: "demo", note: "" });
      setSelectedSlot(null);
    } catch (err) {
      if (err?.response?.status === 409) {
        setStatus({ state: "error", msg: t.contact.errorTaken });
        setSelectedSlot(null);
      } else {
        setStatus({ state: "error", msg: t.contact.errorGeneric });
      }
    }
  };

  return (
    <section id="contact" className="py-24 lg:py-32 bg-stone-100/70">
      <div className="max-w-6xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <div>
          <span className="text-[10px] font-bold tracking-[0.22em] uppercase text-[#1B4332]">
            {t.contact.overline}
          </span>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl font-light tracking-tighter text-stone-900">
            {t.contact.title}
          </h2>
          <p className="mt-5 text-stone-600 leading-relaxed max-w-md">
            {t.contact.sub}
          </p>
          <div className="mt-10 space-y-3 text-sm text-stone-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                <Leaf className="w-4 h-4 text-[#1B4332]" strokeWidth={1.5} />
              </div>
              {t.contact.badge1}
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#D8F3DC] flex items-center justify-center">
                <CheckCircle2 className="w-4 h-4 text-[#1B4332]" strokeWidth={1.5} />
              </div>
              {t.contact.badge2}
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl p-8 md:p-10 border border-stone-200 shadow-sm"
          data-testid="lead-form"
        >
          {/* Slot picker */}
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-1">
              <label className="text-xs font-bold tracking-[0.2em] uppercase text-[#1B4332]">
                {t.contact.slot.heading}
              </label>
              <span className="text-[10px] text-stone-500">
                {t.contact.slot.subheading}
              </span>
            </div>
            <div className="mt-3 border-b border-stone-100 pb-6">
              <SlotPicker t={t} locale={locale} selectedSlot={selectedSlot} onSelect={setSelectedSlot} />
              {selectedSlot && (
                <div
                  className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#D8F3DC] text-xs text-[#1B4332] font-medium"
                  data-testid="slot-selected-chip"
                >
                  <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={1.8} />
                  {t.contact.slot.selected}: {formatSelectedSlot(selectedSlot)}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-5">
            <div>
              <label className="text-xs font-medium tracking-wide text-stone-600">{t.contact.fields.name}</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 bg-stone-50 focus:bg-white focus:border-[#1B4332] focus:outline-none transition-colors text-stone-900"
                placeholder={t.contact.fields.namePh}
                data-testid="lead-name-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium tracking-wide text-stone-600">{t.contact.fields.email}</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 bg-stone-50 focus:bg-white focus:border-[#1B4332] focus:outline-none transition-colors text-stone-900"
                placeholder={t.contact.fields.emailPh}
                data-testid="lead-email-input"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-medium tracking-wide text-stone-600">{t.contact.fields.company}</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 bg-stone-50 focus:bg-white focus:border-[#1B4332] focus:outline-none transition-colors text-stone-900"
                  placeholder={t.contact.fields.companyPh}
                  data-testid="lead-company-input"
                />
              </div>
              <div>
                <label className="text-xs font-medium tracking-wide text-stone-600">{t.contact.fields.interest}</label>
                <select
                  value={form.interest}
                  onChange={(e) => setForm({ ...form, interest: e.target.value })}
                  className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 bg-stone-50 focus:bg-white focus:border-[#1B4332] focus:outline-none transition-colors text-stone-900"
                  data-testid="lead-interest-select"
                >
                  <option value="demo">{t.contact.options.demo}</option>
                  <option value="wind">{t.contact.options.wind}</option>
                  <option value="solar">{t.contact.options.solar}</option>
                  <option value="storage">{t.contact.options.storage}</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-medium tracking-wide text-stone-600">{t.contact.fields.note}</label>
              <textarea
                value={form.note}
                onChange={(e) => setForm({ ...form, note: e.target.value })}
                rows={2}
                className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 bg-stone-50 focus:bg-white focus:border-[#1B4332] focus:outline-none transition-colors text-stone-900 resize-none"
                placeholder={t.contact.fields.notePh}
                data-testid="lead-note-input"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={status.state === "loading"}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#1B4332] text-stone-50 font-medium hover:bg-[#2D6A4F] transition-all disabled:opacity-60"
            data-testid="lead-submit-button"
          >
            {status.state === "loading" ? t.contact.submitting : t.contact.submit}
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </button>

          {status.state === "ok" && (
            <p className="mt-4 text-sm text-[#1B4332] flex items-center gap-2" data-testid="lead-success-message">
              <CheckCircle2 className="w-4 h-4" strokeWidth={1.6} /> {status.msg}
            </p>
          )}
          {status.state === "error" && (
            <p className="mt-4 text-sm text-red-700" data-testid="lead-error-message">
              {status.msg}
            </p>
          )}
        </form>
      </div>
    </section>
  );
}

/* -------------------- Footer -------------------- */
function Footer({ t }) {
  return (
    <footer className="bg-[#081C13] text-stone-300 py-16">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-2 md:grid-cols-4 gap-10">
        <div className="col-span-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[#2D6A4F] flex items-center justify-center">
              <Cpu className="w-4 h-4 text-[#D8F3DC]" strokeWidth={1.6} />
            </div>
            <span className="font-display text-xl text-stone-50">
              {t.brand.name}
              <span className="text-[#95D5B2]">{t.brand.suffix}</span>
            </span>
          </div>
          <p className="mt-5 text-sm max-w-sm text-stone-400 leading-relaxed">
            {t.footer.tagline}
          </p>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-stone-500 mb-4">{t.footer.product}</div>
          <ul className="space-y-2 text-sm">
            {t.footer.productLinks.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-stone-500 mb-4">{t.footer.company}</div>
          <ul className="space-y-2 text-sm">
            {t.footer.companyLinks.map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 mt-12 pt-6 border-t border-stone-800 flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
        <span className="text-xs text-stone-500">{t.footer.copyright}</span>
        <span className="text-xs text-stone-500">{t.footer.madeWith}</span>
      </div>
    </footer>
  );
}

/* -------------------- Page -------------------- */
export default function Landing() {
  const [stats, setStats] = useState(null);
  const { locale, t, toggle } = useI18n();

  useEffect(() => {
    let alive = true;
    const fetchStats = async () => {
      try {
        const res = await axios.get(`${API}/stats/live`);
        if (alive) setStats(res.data);
      } catch (e) {
        // silent
      }
    };
    fetchStats();
    const id = setInterval(fetchStats, 4000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, []);

  const scrollToContact = () => {
    const el = document.getElementById("contact");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-stone-50" data-testid="landing-root">
      <Nav onCtaClick={scrollToContact} t={t} locale={locale} toggle={toggle} />
      <Hero stats={stats} onCtaClick={scrollToContact} t={t} locale={locale} />
      <ComparisonStrip t={t} />
      <LogosStrip t={t} />
      <Platform t={t} />
      <Impact t={t} />
      <HowItWorks t={t} />
      <ContactForm t={t} locale={locale} />
      <Footer t={t} />
    </div>
  );
}
